// Shared incident form body.
//
// Rendered by both the portal form (/incident/form) and the in-app webview
// form (/noauth/create/incident) so the two surfaces stay identical. This
// component owns layout only — every piece of state, every API call and all
// validation stay in the page components.
//
// Field order is the design. It follows how an incident is actually reported:
//   1. WHERE  — locality / sub-locality (these gate everything downstream)
//   2. WHAT   — the locality-defined type field
//   3. PIN    — address + map
//   4. DETAIL — description, extra fields, responding units
//   5. SEND   — push, featured, social
// Submit lives in a fixed bar at the bottom of the viewport and is live from
// the first render, so an admin can post the essentials in seconds and come
// back to fill in the rest.
import { Fragment, useState } from "react";
import { Form } from "react-bootstrap";

import FieldSelect from "elements/System/FieldSelect";
import MultiFieldSelect from "elements/System/MultiFieldSelect";
import GoogleMaps from "elements/System/GoogleMaps";
import Facebook from "elements/System/Facebook";
import { FwSection, FwField, FwToggle, FwCheck } from "./Primitives";

type Props = {
  isEdit: boolean;
  /** Auth token override — the webview form passes its query-string token. */
  auth?: string;
  authRole?: string;

  fields: any;
  errors: any;
  handleChange: (e: any) => void;
  setValue: (key: string, value: any) => void;
  setMultiValue: (values: any) => void;

  formReady: boolean;
  finder: any;
  setEffectField: (v: any) => void;

  locality: any;
  locDefinition: any;
  onLocalitySelect: (data: any) => void;

  subLocalityQuery: string;
  respondingUnitsQuery: string;
  fieldOptionQuery: string;
  memoizedRespondingUnits: any[];

  facebookPages: any[];
  twitterPage: any;
  onFacebookPagesLinked: (userAccessToken: string) => void;
  onFacebookPageCheck: (pageId: any, checked: boolean) => void;
  onTwitterCheck: (checked: boolean) => void;

  /** Fields the dispatch parser filled in but wasn't sure about, keyed by form
   *  field name. Advisory highlight only — it never blocks submission. */
  needsCheck?: Record<string, boolean>;
  /** An address for the map to look up and pin — see GoogleMaps.searchAddress. */
  searchAddress?: string;
};

export default function IncidentFormFields(props: Props) {
  const {
    isEdit,
    auth,
    authRole,
    fields,
    errors,
    handleChange,
    setValue,
    formReady,
    finder,
    setEffectField,
    locality,
    locDefinition,
    onLocalitySelect,
    subLocalityQuery,
    respondingUnitsQuery,
    fieldOptionQuery,
    memoizedRespondingUnits,
    facebookPages,
    twitterPage,
    onFacebookPagesLinked,
    onFacebookPageCheck,
    onTwitterCheck,
    needsCheck,
  } = props;

  // Details opens by default — nothing is hidden behind a wizard step.
  //
  // Delivery is open on an update too. It used to start closed so re-notifying
  // wasn't something to trip over, but the toggle kept whatever value the
  // incident was saved with — so an incident created with push on re-alerted
  // every follower on save, from a section the admin never saw. The toggle now
  // defaults to off when editing (see the form pages) and the section stays
  // visible, because an escalating incident — 2nd alarm going to 3rd — SHOULD
  // push again, and that is the admin's call to make on purpose.
  const [openDetail, setOpenDetail] = useState(true);
  const [openDelivery, setOpenDelivery] = useState(true);

  const hasLocality = Boolean(locality?.value);
  const socialLocality =
    hasLocality &&
    (locality.label === "New York City" || locality.label === "Long Island");

  const extraTextFields = [
    { key: "field3Value", name: locDefinition?.field3Name },
    { key: "field4Value", name: locDefinition?.field4Name },
    { key: "field5Value", name: locDefinition?.field5Name },
  ].filter((f) => Boolean(f.name));

  const unitCount = memoizedRespondingUnits?.length || 0;

  return (
    <div className="fw-incident__form">
      {/* ── 1 + 2: where, and what ─────────────────────────────────── */}
      <FwSection title="Incident" static>
        <FwField label="Locality" required error={errors.locality} needsCheck={needsCheck?.locality}>
          <div className="fw-select">
            <FieldSelect
              api="admin/locality"
              query="?offset=1&limit=1000"
              get="name"
              set="_id"
              auth={auth}
              value={fields?.locality}
              placeholder="Type to search localities…"
              render={"yes"}
              finder={finder?.locality}
              multi={false}
              onSelect={onLocalitySelect}
            />
          </div>
        </FwField>

        {formReady && hasLocality && (
          <FwField label="Sub Locality" required error={errors.subLocality} needsCheck={needsCheck?.subLocality}>
            <div className="fw-select">
              <FieldSelect
                api="admin/sub-locality"
                query={subLocalityQuery}
                get="name"
                set="_id"
                auth={auth}
                value={fields?.subLocality}
                placeholder="Type to search sub-localities…"
                render={formReady && hasLocality ? "yes" : "no"}
                multi={false}
                onSelect={(data: any) => {
                  if (!data || data.value === fields?.subLocality?.value) return;
                  setEffectField(["subLocality", data]);
                }}
                locality={locality}
              />
            </div>
          </FwField>
        )}

        {/* field1 is the locality-defined type list (e.g. "Title" in NYC) */}
        {formReady && hasLocality && locDefinition?.field1Name && (
          <FwField label={locDefinition.field1Name} needsCheck={needsCheck?.field1Value}>
            <div className="fw-select">
              <FieldSelect
                api={`admin/prefilled-field-option/${locality?.value}`}
                creatable={false}
                query={fieldOptionQuery}
                get="optionName"
                set="optionName"
                auth={auth}
                value={fields?.field1Value}
                placeholder={`Type to search ${locDefinition.field1Name.toLowerCase()}…`}
                render={formReady && hasLocality ? "yes" : "no"}
                clearable={false}
                finder={finder?.field1Value}
                multi={false}
                onSelect={(data: any) => setEffectField(["field1Value", data])}
                locality={locality}
              />
            </div>
          </FwField>
        )}
      </FwSection>

      {/* ── 3: pin it ───────────────────────────────────────────────── */}
      <FwSection
        title="Location"
        static
        required
        needsCheck={needsCheck?.location}
      >
        <div className="fw-map">
          {((fields.latitude && fields.longitude && isEdit) || !isEdit) && (
            <GoogleMaps
              searchAddress={props.searchAddress}
              latLng={{
                lat: parseFloat(fields.latitude),
                lng: parseFloat(fields.longitude),
              }}
              onSuccess={(data: any) =>
                props.setMultiValue({
                  address: data.address,
                  latitude: data.lat,
                  longitude: data.lng,
                })
              }
            />
          )}
        </div>

        {(errors.latitude || errors.longitude) && (
          <p className="fw-field__error">
            Drop a pin or search an address to set the location.
          </p>
        )}

        <div className="fw-latlng">
          <div className="fw-latlng__item">
            <span>Latitude</span>
            <strong>{fields.latitude || "—"}</strong>
          </div>
          <div className="fw-latlng__item">
            <span>Longitude</span>
            <strong>{fields.longitude || "—"}</strong>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <FwField label="Street" htmlFor="fw-address" needsCheck={needsCheck?.address}>
            <Form.Control
              type="text"
              id="fw-address"
              name="address"
              maxLength={100}
              placeholder="e.g. 570 W 156th St off Amsterdam Ave"
              value={fields.address}
              onChange={handleChange}
            />
          </FwField>
        </div>
      </FwSection>

      {/* ── 4: the detail you add now or a minute later ─────────────── */}
      <FwSection
        title="Details"
        hint={unitCount ? `${unitCount} unit${unitCount > 1 ? "s" : ""}` : "optional"}
        open={openDetail}
        onToggle={() => setOpenDetail((v) => !v)}
      >
        {locDefinition?.field2Name && (
          <FwField label={locDefinition.field2Name} htmlFor="fw-field2" needsCheck={needsCheck?.field2Value}>
            <Form.Control
              as="textarea"
              id="fw-field2"
              name="field2Value"
              maxLength={10000}
              rows={4}
              placeholder="What is happening on scene?"
              value={fields.field2Value}
              onChange={handleChange}
            />
          </FwField>
        )}

        {extraTextFields.map((f) => (
          <FwField key={f.key} label={f.name} htmlFor={`fw-${f.key}`} needsCheck={needsCheck?.[f.key]}>
            <Form.Control
              type="text"
              id={`fw-${f.key}`}
              name={f.key}
              maxLength={100}
              value={fields[f.key]}
              onChange={handleChange}
            />
          </FwField>
        ))}

        {formReady && hasLocality && (
          <FwField
            label="Responding Units"
            needsCheck={needsCheck?.respondingUnits}
            action={
              unitCount
                ? {
                    label: "Clear",
                    onClick: () => setEffectField(["respondingUnits", []]),
                  }
                : undefined
            }
          >
            <div className="fw-select">
              <MultiFieldSelect
                api={`admin/prefilled-locality-unit/${locality?.value}`}
                creatable={authRole === "super"}
                query={respondingUnitsQuery}
                get="unitName"
                set="unitName"
                auth={auth}
                value={memoizedRespondingUnits}
                placeholder="Type to search units…"
                render={formReady && hasLocality ? "yes" : "no"}
                clearable={false}
                finder={finder?.respondingUnits}
                multi={true}
                onSelect={(data: any) =>
                  setEffectField(["respondingUnits", data])
                }
                locality={locality}
              />
            </div>
          </FwField>
        )}

        {!locDefinition?.field2Name && !extraTextFields.length && !hasLocality && (
          <p className="fw-field__error" style={{ color: "var(--fw-ink-muted)" }}>
            Pick a locality to see its detail fields.
          </p>
        )}
      </FwSection>

      {/* ── 5: how it goes out ──────────────────────────────────────── */}
      <FwSection
        title="Delivery"
        hint={fields.sendPushNotification ? "push ON" : "push off"}
        open={openDelivery}
        onToggle={() => setOpenDelivery((v) => !v)}
      >
        <FwToggle
          title="Send push notification"
          sub={
            isEdit
              ? "Off by default on an update. Turn it on to alert followers again — e.g. when the alarm escalates."
              : "Alerts every follower of this locality"
          }
          checked={Boolean(fields.sendPushNotification)}
          onChange={(next) => setValue("sendPushNotification", next)}
        />

        {/* On an update the push is the one thing on this form that reaches
            people outside the room, so say so plainly rather than leaving it
            to a toggle the admin may not look twice at. */}
        {isEdit && fields.sendPushNotification && (
          <p className="fw-field__error" style={{ marginTop: 4 }}>
            Saving will send a new push notification to everyone following this
            locality.
          </p>
        )}
        {/* The old copy here read "Pins this incident to the top of the feed",
            which nothing does — the app feed sorts purely by createdAt and never
            reads this flag. What it actually controls is the "Set Featured"
            button on comment photos, and clearing it wipes featuredImageUrl on
            save. Described as what it does, pending a separate decision on
            whether real pinning gets built. */}
        <FwToggle
          title="Featured"
          sub="Lets a comment photo become this incident's main image in the apps. Turning it off clears the current one."
          checked={Boolean(fields.featured)}
          onChange={(next) => setValue("featured", next)}
        />

        {socialLocality && (
          <Fragment>
            <div style={{ marginTop: 18 }}>
              <Facebook setFBPage={onFacebookPagesLinked} />
            </div>
            {facebookPages.length > 0 ? (
              <FwField label="Post to Facebook page(s)">
                {facebookPages.map((obj: any, idx: number) => (
                  <FwCheck
                    key={obj.pageId || idx}
                    label={obj.pageName}
                    checked={Boolean(obj?.checked)}
                    onChange={(next) => onFacebookPageCheck(obj.pageId, next)}
                  />
                ))}
              </FwField>
            ) : (
              <p className="fw-field__error" style={{ color: "var(--fw-ink-muted)" }}>
                No active Facebook pages.
              </p>
            )}
          </Fragment>
        )}

        {hasLocality && twitterPage?.twitterPage && (
          <FwField label="Post to X / Twitter">
            <FwCheck
              label={twitterPage.twitterPage}
              checked={Boolean(twitterPage?.checked)}
              onChange={onTwitterCheck}
            />
          </FwField>
        )}
      </FwSection>
    </div>
  );
}
